import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@journal/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Avatar storage key (`avatars/{userId}/{hash}.webp`).
   *
   * It lives here and is intentionally not read from the session: the API serves
   * the session and caches it, so a newly uploaded photo would not appear until
   * the next refresh. Reading it from the local row —like the language, font,
   * and theme— makes the photo appear instantly and work offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * It ALWAYS filters by `userId`, like the rest of the `user` reads in this
 * module. It previously chose the filter in one branch and the guest was left with
 * `SELECT … FROM user LIMIT 1` bare: without `WHERE`, SQLite returns the FIRST
 * row it likes, which need not be the guest's row. That is not a correctness
 * failure but a data leak between accounts: during the guest→real upgrade, the
 * local database downloads one `user` row for each fellow member of the organization,
 * so the guest ended up applying ANOTHER person's theme. Without `WHERE`,
 * an empty result is impossible: someone always returns a row.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
